import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { GoogleSignInButton } from '../components/GoogleSignInButton';
import { Phone, ArrowRight, Loader2, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { billingAPI, authAPI } from '../lib/api';

const PLAN_LABELS = {
  free: { name: 'Free Plan', desc: '10-minute lifetime trial, no credit card required' },
  standard: { name: 'Standard Plan', desc: '₹2,999/mo · 180 min · All Indian Languages' },
  premium: { name: 'Premium Plan', desc: '₹5,999/mo · 285 min · 5 agents' },
  enterprise: { name: 'Enterprise Plan', desc: '₹15,999/mo · 1700 min · Best Voice Quality' },
};

export default function RegisterPage() {
  const [form, setForm] = useState({ name: '', email: '', password: '', company: '', phone: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register, googleLogin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const selectedPlan = searchParams.get('plan') || 'free';
  const paymentSuccess = searchParams.get('payment_success') === 'true';
  const requirePayment = searchParams.get('require_payment') === 'true';
  const planInfo = PLAN_LABELS[selectedPlan.toLowerCase()] || PLAN_LABELS.free;

  // Load Razorpay script for payment flow
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const plan = selectedPlan || 'free';
      
      // If payment already completed, verify and register
      if (paymentSuccess) {
        const paymentData = JSON.parse(localStorage.getItem('registration_payment') || '{}');
        
        if (!paymentData.razorpay_order_id) {
          throw new Error('Payment verification data missing');
        }

        // Verify payment and create account
        const res = await billingAPI.verifyRegistrationPayment({
          ...paymentData,
          password: form.password
        });

        localStorage.removeItem('registration_payment');
        localStorage.setItem('dotcall_token', res.data.token);
        localStorage.setItem('dotcall_user', JSON.stringify(res.data.user));
        toast.success('Account created successfully!');
        navigate('/dashboard');
        return;
      }

      // Enterprise plan ALWAYS requires payment - enforce it
      if (plan === 'enterprise') {
        await handlePaidPlanRegistration(plan);
        return;
      }

      // Check if this is a paid plan requiring payment first
      if (requirePayment && plan !== 'free') {
        await handlePaidPlanRegistration(plan);
        return;
      }

      // Regular registration for free plan
      await register({ ...form, plan });
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePaidPlanRegistration = async (planId) => {
    // Validate form first
    if (!form.name || !form.email || !form.password) {
      setError('Please fill in all required fields');
      setLoading(false);
      return;
    }

    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Payment gateway failed to load');
        setLoading(false);
        return;
      }

      // Create registration order
      const orderRes = await billingAPI.createRegistrationOrder({
        plan_id: planId,
        email: form.email,
        name: form.name,
        company: form.company,
        phone: form.phone
      });

      // Handle demo mode
      if (orderRes.data.demo_mode || orderRes.data.free) {
        await register({ ...form, plan: planId });
        navigate('/dashboard');
        return;
      }

      // Open Razorpay
      const plans = await billingAPI.plans();
      const plan = plans.data.find(p => p.id === planId);

      const options = {
        key: orderRes.data.key_id,
        amount: orderRes.data.amount,
        currency: orderRes.data.currency,
        name: 'DotCall',
        description: orderRes.data.is_enterprise
          ? `Enterprise Plan - First Wallet Refill (₹${orderRes.data.amount / 100})`
          : `${plan?.name || planId} Plan Subscription`,
        order_id: orderRes.data.id,
        handler: async (response) => {
          // Payment successful - verify and create account
          try {
            const res = await billingAPI.verifyRegistrationPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              password: form.password
            });

            localStorage.setItem('dotcall_token', res.data.token);
            localStorage.setItem('dotcall_user', JSON.stringify(res.data.user));
            toast.success('Payment successful! Welcome to DotCall');
            navigate('/dashboard');
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Registration failed');
            setLoading(false);
          }
        },
        prefill: {
          email: form.email,
          name: form.name,
          contact: form.phone
        },
        theme: { color: '#d97706' },
        modal: {
          ondismiss: () => {
            setLoading(false);
            toast.error('Payment cancelled');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment initialization failed');
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credential) => {
    setLoading(true);
    try {
      const result = await googleLogin(credential);
      
      if (result.is_new_user) {
        // If a plan is already selected (user came from /pricing), proceed directly
        if (selectedPlan && selectedPlan !== 'free') {
          // Paid plan already selected — proceed with payment + Google registration
          const plan = (await billingAPI.plans()).data.find(p => p.id === selectedPlan);
          if (plan) {
            await handleGooglePaidPlanCheckout(selectedPlan, plan, result);
            return;
          }
        } else if (selectedPlan === 'free') {
          // Free plan — complete Google registration directly
          try {
            const googleData = JSON.parse(localStorage.getItem('google_pending') || '{}');
            const res = await authAPI.completeGoogleRegistration({
              google_data: googleData,
              plan: 'free'
            });
            localStorage.setItem('dotcall_token', res.data.token);
            localStorage.setItem('dotcall_user', JSON.stringify(res.data.user));
            localStorage.removeItem('google_pending');
            toast.success('Account created successfully!');
            navigate('/dashboard');
            return;
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Registration failed');
            setLoading(false);
            return;
          }
        }
        
        // No plan selected — redirect to pricing to choose
        toast.info('Please select a plan to complete registration');
        navigate('/pricing?source=google');
        return;
      }
      
      toast.success('Signed in with Google');
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Google sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGooglePaidPlanCheckout = async (planId, plan, googleResult) => {
    try {
      const googleData = JSON.parse(localStorage.getItem('google_pending') || '{}');

      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Payment gateway failed to load');
        setLoading(false);
        return;
      }

      // Create registration order for Google user
      const orderRes = await billingAPI.createRegistrationOrder({
        plan_id: planId,
        email: googleData.email || '',
        name: googleData.name || '',
        company: '',
        phone: ''
      });

      // Handle demo mode
      if (orderRes.data.demo_mode || orderRes.data.free) {
        const res = await authAPI.completeGoogleRegistration({
          google_data: googleData,
          plan: planId
        });
        localStorage.setItem('dotcall_token', res.data.token);
        localStorage.setItem('dotcall_user', JSON.stringify(res.data.user));
        localStorage.removeItem('google_pending');
        toast.success('Account created successfully!');
        navigate('/dashboard');
        return;
      }

      // Open Razorpay checkout
      const options = {
        key: orderRes.data.key_id,
        amount: orderRes.data.amount,
        currency: orderRes.data.currency,
        name: 'DotCall',
        description: `${plan.name} Plan Subscription`,
        order_id: orderRes.data.id,
        handler: async (response) => {
          try {
            const res = await authAPI.completeGoogleRegistration({
              google_data: googleData,
              plan: planId,
              payment_data: {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              }
            });
            localStorage.setItem('dotcall_token', res.data.token);
            localStorage.setItem('dotcall_user', JSON.stringify(res.data.user));
            localStorage.removeItem('google_pending');
            toast.success('Payment successful! Welcome to DotCall');
            navigate('/dashboard');
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Registration failed');
          }
          setLoading(false);
        },
        prefill: { email: googleData.email, name: googleData.name },
        theme: { color: '#d97706' },
        modal: {
          ondismiss: () => {
            setLoading(false);
            toast.error('Payment cancelled');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment initialization failed');
      setLoading(false);
    }
  };

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #020617 0%, #0B1221 50%, #1a0533 100%)' }}>
        <div className="absolute inset-0" style={{ background: 'radial-gradient(circle at 70% 60%, rgba(6, 182, 212, 0.1) 0%, transparent 60%)' }} />
        <div className="relative z-10 p-16 max-w-lg">
          <div className="flex items-center gap-3 mb-12">
            <img src="https://i.ibb.co/ZRpFcYDK/oie-s-XZ9-BUTHM5-Vz.png" alt="DotCall" className="h-9 w-auto" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold font-['Outfit'] text-white leading-tight mb-6">
            Start Building<br />
            <span className="text-cyan-400">AI Agents Today</span>
          </h1>
          <p className="text-slate-400 text-lg leading-relaxed">
            Join thousands of businesses using AI voice agents to automate 
            their sales and support operations.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8" style={{ background: '#020617' }}>
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <img src="https://i.ibb.co/ZRpFcYDK/oie-s-XZ9-BUTHM5-Vz.png" alt="DotCall" className="h-8 w-auto" />
          </div>

          <h2 className="text-3xl font-bold font-['Outfit'] text-white mb-2">Create your account</h2>
          <p className="text-slate-400 mb-8">Get started for free</p>

          {planInfo && (
            <div className="mb-6 p-4 rounded-xl bg-amber-600/[0.08] border border-amber-500/25 flex items-start gap-3" data-testid="selected-plan-banner">
              <div className="w-9 h-9 rounded-full bg-amber-600/25 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Crown className="w-4 h-4 text-amber-300" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-amber-300/80 mb-0.5">Selected plan</div>
                <div className="text-white font-semibold text-sm">{planInfo.name}</div>
                <div className="text-xs text-white/50 mt-0.5">{planInfo.desc}</div>
                {paymentSuccess && (
                  <div className="text-xs text-emerald-400 mt-1 flex items-center gap-1">
                    ✓ Payment completed
                  </div>
                )}
              </div>
              <button type="button" onClick={() => navigate('/pricing')} disabled={paymentSuccess} className="text-xs text-amber-300 hover:text-amber-200 underline whitespace-nowrap disabled:opacity-50">
                {paymentSuccess ? 'Paid' : 'Change'}
              </button>
            </div>
          )}

          {error && (
            <div className="mb-6 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="register-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-slate-300 text-sm mb-2 block">Full Name</Label>
              <Input
                value={form.name}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder="John Smith"
                required
                data-testid="register-name-input"
                className="bg-slate-950 border-white/10 focus:border-amber-500/50 h-11 text-white placeholder:text-slate-600"
              />
            </div>
            <div>
              <Label className="text-slate-300 text-sm mb-2 block">Work Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => updateField('email', e.target.value)}
                placeholder="you@company.com"
                required
                data-testid="register-email-input"
                className="bg-slate-950 border-white/10 focus:border-amber-500/50 h-11 text-white placeholder:text-slate-600"
              />
            </div>
            <div>
              <Label className="text-slate-300 text-sm mb-2 block">Company (Optional)</Label>
              <Input
                value={form.company}
                onChange={(e) => updateField('company', e.target.value)}
                placeholder="Acme Inc."
                data-testid="register-company-input"
                className="bg-slate-950 border-white/10 focus:border-amber-500/50 h-11 text-white placeholder:text-slate-600"
              />
            </div>
            <div>
              <Label className="text-slate-300 text-sm mb-2 block">Phone (Optional)</Label>
              <Input
                type="tel"
                value={form.phone}
                onChange={(e) => updateField('phone', e.target.value)}
                placeholder="+91 98765 43210"
                data-testid="register-phone-input"
                className="bg-slate-950 border-white/10 focus:border-amber-500/50 h-11 text-white placeholder:text-slate-600"
              />
            </div>
            <div>
              <Label className="text-slate-300 text-sm mb-2 block">Password</Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => updateField('password', e.target.value)}
                placeholder="Min 6 characters"
                required
                minLength={6}
                data-testid="register-password-input"
                className="bg-slate-950 border-white/10 focus:border-amber-500/50 h-11 text-white placeholder:text-slate-600"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="register-submit-button"
              className="w-full h-11 bg-amber-600 hover:bg-amber-700 text-white font-medium shadow-[0_0_20px_rgba(245,158,11,0.3)] hover:shadow-[0_0_30px_rgba(245,158,11,0.5)] transition-all mt-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Create Account <ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-[#020617] px-3 text-slate-500">or</span></div>
          </div>

          <GoogleSignInButton onSuccess={handleGoogleSuccess} onError={(e) => setError(e)} text="signup_with" />

          <p className="text-center text-slate-500 mt-8 text-sm">
            Already have an account?{' '}
            <Link to="/login" className="text-amber-400 hover:text-amber-300" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
